import { useState } from 'react';
import type { Calendar, EventInput } from '../../shared/contracts';
import { api, type ClientSession } from '../api';
import { formatEventRange } from '../format';

export type ImportChange = {
  uid: string;
  id: string | null;
  version: number | null;
  action: 'create' | 'update';
  changedFields: string[];
};
export type ImportPreview = { events: EventInput[]; warnings: string[]; changes: ImportChange[] };

export function ImportPage({
  calendars,
  session,
}: {
  calendars: Calendar[];
  session: ClientSession | null;
}) {
  const destinations = calendars.filter(
    (calendar) => calendar.canEdit && calendar.source === 'local',
  );
  const [calendarId, setCalendarId] = useState(destinations[0]?.id ?? '');
  const [ics, setIcs] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  if (!session?.user || !destinations.length)
    return (
      <main id="main-content" className="narrow-page">
        <h1>Import a calendar file</h1>
        <p>Sign in with permission to edit a calendar to import events.</p>
        <a className="button" href="/login">
          Sign in
        </a>
      </main>
    );
  const readFile = async (file?: File) => {
    setPreview(null);
    setIcs('');
    setError('');
    setStatus('');
    if (!file) return;
    if (file.size > 200000) {
      setError('Choose a calendar file smaller than 200 KB.');
      return;
    }
    setBusy(true);
    try {
      setIcs(await file.text());
    } catch {
      setError('The file could not be read. Please choose it again.');
    } finally {
      setBusy(false);
    }
  };
  const review = async () => {
    setBusy(true);
    setError('');
    setStatus('');
    setPreview(null);
    try {
      setPreview(await api.previewImport({ calendarId, ics }, session.csrfToken));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The file could not be reviewed.');
    } finally {
      setBusy(false);
    }
  };
  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.commitImport(
        {
          calendarId,
          ics,
          expectedVersions: preview.changes.map(({ uid, id, version }) => ({ uid, id, version })),
        },
        session.csrfToken,
      );
      setStatus(
        `${result.imported} event${result.imported === 1 ? '' : 's'} added, ${result.updated} updated.`,
      );
      setPreview(null);
      setIcs('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The import could not be completed.');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main id="main-content" className="settings-page">
      <a className="back-link" href="/settings">
        ← Settings
      </a>
      <span className="eyebrow">Bring your plans together</span>
      <h1>Import a calendar file</h1>
      <p>
        {session.capabilities.superAdmin
          ? 'You can bulk-import past events as historical placeholders. Existing events cannot be rescheduled into the past.'
          : 'Import upcoming events. Only a Super Admin can add historical events.'}
      </p>
      <section className="settings-card">
        <p>
          Choose an iCalendar (.ics) file, then review its events before adding or updating them.
          Existing poster pages are preserved. Files may contain up to 100 events.
        </p>
        <label className="field">
          <span>Destination calendar</span>
          <select
            disabled={busy}
            value={calendarId}
            onChange={(event) => {
              setCalendarId(event.target.value);
              setPreview(null);
            }}
          >
            {destinations.map((calendar) => (
              <option key={calendar.id} value={calendar.id}>
                {calendar.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Calendar file</span>
          <input
            type="file"
            accept=".ics,text/calendar"
            disabled={busy}
            onChange={(event) => void readFile(event.target.files?.[0])}
          />
        </label>
        <p className="field-help">Times without a timezone use America/Los_Angeles.</p>
        {error && (
          <p className="inline-error" role="alert">
            {error}
          </p>
        )}
        {status && (
          <p role="status">
            {status} <a href="/">Open calendar</a>
          </p>
        )}
        <button
          className="button"
          disabled={busy || !ics || !calendarId}
          onClick={() => void review()}
        >
          {busy ? 'Working…' : 'Review import'}
        </button>
      </section>
      {preview && (
        <section className="settings-card">
          <h2>Review {preview.events.length} events</h2>
          {preview.warnings.map((warning, index) => (
            <p key={index} className="settings-warning">
              {warning}
            </p>
          ))}
          <ul>
            {preview.events.map((event, index) => (
              <li key={preview.changes[index]?.uid ?? index}>
                <strong>{event.title}</strong>
                <p>
                  {formatEventRange(event)} ({event.timezone})
                </p>
                <p>
                  {preview.changes[index]?.action === 'update'
                    ? `Update existing event: ${preview.changes[index]?.changedFields.join(', ') || 'no changed fields'}`
                    : 'Add new event'}
                </p>
              </li>
            ))}
          </ul>
          <button className="button" disabled={busy} onClick={() => void commit()}>
            Import reviewed events
          </button>
        </section>
      )}
    </main>
  );
}
